import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'
import { StringParam, useQueryParam } from 'use-query-params'

import { useBreadcrumbs } from '../../../layout/breadcrumbsContext/useBreadcrumbs/useBreadcrumbs'
import { RoutePath } from '../../../layout/Routes'

import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import { useAuthState, useQuery, useRequiredParams } from '@percent/partner-dashboard/common/hooks'
import { ErrorView } from '@percent/partner-dashboard/common/components'
import { Loader, Grid, Typography, Tabs, Card, Flex } from '@percent/lemonade'
import { EmployeeAchievements } from './EmployeeAchievements/EmployeeAchievements'
import styles from './EmployeeDetailsPage.module.scss'
import { EmployeeActivity } from './EmployeeActivity/EmployeeActivity'
import { EmployeeWallet } from './EmployeeWallet/EmployeeWallet'

export function EmployeeDetailsPage() {
  const { partner } = useAuthState()
  const { t } = useTranslation()
  const { employeeId } = useRequiredParams()
  const [tabQP, setTabQP] = useQueryParam('tab', StringParam)
  const { workplaceGivingService } = useServices()

  const hasGamification = partner?.scopes?.some(data => data.scopeName.includes('gamification'))
  const hasDonationWallet = partner?.scopes?.some(data => data.scopeName.includes('donation_wallet'))

  const [{ data: employeeData, errorMessage: employeeDetailsErrorMessage, isLoading: isEmployeeDataLoading }] =
    useQuery(workplaceGivingService.getEmployeeDetails, employeeId)

  useBreadcrumbs([
    {
      label: t('breadcrumb.employees'),
      path: RoutePath.WORKPLACE_GIVING_EMPLOYEES
    },
    ...(employeeData?.fullName ? [{ label: employeeData.fullName }] : [])
  ])

  const tabs = useMemo(
    () => [
      ...(hasGamification ? [{ key: 'rewards' as const, label: t('typography.employeeDetails.tabs.rewards') }] : []),
      { key: 'activity' as const, label: t('typography.employeeDetails.tabs.activity') },
      ...(hasDonationWallet ? [{ key: 'wallet' as const, label: t('typography.employeeDetails.tabs.wallet') }] : [])
    ],
    [hasGamification, hasDonationWallet, t]
  )

  const activeTab = useMemo(() => {
    if (!tabQP) return tabs[0].key

    return tabs.find(tab => tab.key === tabQP)?.key ?? tabs[0].key
  }, [tabs, tabQP])

  if (isEmployeeDataLoading) {
    return <Loader />
  }

  if (!employeeData || employeeDetailsErrorMessage) {
    return (
      <Card>
        <Flex w="100%" justify="center">
          <ErrorView errorMessage={employeeDetailsErrorMessage} />
        </Flex>
      </Card>
    )
  }

  return (
    <Grid gutter={16}>
      <Grid.Col span={12}>
        <Card className={styles.header}>
          <Flex direction="column">
            <div className={styles.headerTitleWrapper}>
              <Typography variant="h3" color="secondary">
                {employeeData.fullName}
              </Typography>
            </div>
            <Tabs items={tabs} activeTab={activeTab} onChangeTab={setTabQP} />
          </Flex>
        </Card>
      </Grid.Col>
      <Grid.Col span={12}>
        {activeTab === 'wallet' ? (
          <EmployeeWallet accountId={employeeId} />
        ) : (
          <Card className={styles.main}>
            {activeTab === 'rewards' && (
              <Flex direction="column" style={{ width: '100%' }}>
                <EmployeeAchievements accountId={employeeId} />
              </Flex>
            )}
            {activeTab === 'activity' && (
              <Flex direction="column" style={{ width: '100%' }}>
                <EmployeeActivity accountId={employeeId} />
              </Flex>
            )}
          </Card>
        )}
      </Grid.Col>
    </Grid>
  )
}
